import { IEvidence, ProgramData, ProgramSkill } from "@interfaces/Interfaces";
import React, { useMemo } from "react";
import _ from "lodash";
import ProgramInfoCardButton from "@components/home/ProgramInfoCardButton";
import { useHistory } from "react-router-dom";
import * as StringUtils from "@utils/stringUtils";
import * as ProgramUtils from "@utils/programUtils";
import { useRecoilValueLoadable } from "recoil";
import { evidenceAtom } from "@state/State";
import SkillsRAGDisplay from "@components/programs/SkillsRAGDisplay";
import SkillsRAGDisplayRenfrew from "@components/programs/SkillsRAGDisplayRenfrew";

type Props = {
  program: ProgramData;
  currentLevel: string;
};

const ProgramSkillsMap: React.FC<Props> = (props) => {
  const history = useHistory();
  const allEvidence = useRecoilValueLoadable<IEvidence[]>(evidenceAtom);

  const skills = useMemo(
    () => ProgramUtils.filterSkillsByLevel(props.program.Skills ?? [], props.currentLevel),
    [props.program.Skills, props.currentLevel]
  );

  const programEvidence = useMemo(() => {
    if (allEvidence.state !== "hasValue" || !allEvidence.contents) return [];
    return allEvidence.contents.filter((e: IEvidence) => {
      const programInfo = e.evidenceJSON?.programInfo;
      return programInfo?.programID === props.program.ID && !e.draft;
    });
  }, [allEvidence.state, allEvidence.contents, props.program.ID]);

  const skillMap = useMemo(() => {
    return _.groupBy(skills, (skill) => skill.Section);
  }, [skills]);

  const handleSkillClick = (skills: ProgramSkill[], section: string) => {
    if (ProgramUtils.isRenfrew(props.program.ID)) {
      history.push(`/dashboard/program/${props.program.ID}/skills/${StringUtils.toSlug(section)}`);
    } else {
      history.push(`/dashboard/program/${props.program.ID}/program-skills-log/${StringUtils.toSlug(section)}`);
    }
  };

  return (
    <div>
      {_.map(skillMap, (sectionSkills, section) => {
        const sectionSkillIds = new Set(sectionSkills.map((s) => s.ID));
        const sectionEvidence = programEvidence.filter((e) => {
          if (ProgramUtils.isRenfrew(props.program.ID)) {
            return sectionSkills.some((skill) => ProgramUtils.evidenceMatchesSkill(e, skill, props.program));
          }

          return sectionSkillIds.has(e.evidenceJSON?.programInfo?.skillID);
        });

        const subTitle =
          ProgramUtils.isRenfrew(props.program.ID) && section === "CPGs"
            ? "Evidence each CPG once a year to stay compliant."
            : props.program.SkillsCardSubtitle ?? undefined;

        return (
          <div key={section}>
            <ProgramInfoCardButton
              title={section}
              subTitle={subTitle}
              buttonPosition="top"
              buttonClicked={() => handleSkillClick(sectionSkills, section)}
            >
              {ProgramUtils.isRenfrew(props.program.ID) ? (
                <SkillsRAGDisplayRenfrew
                  skills={sectionSkills}
                  evidence={sectionEvidence}
                  program={props.program}
                  section={section}
                />
              ) : (
                <SkillsRAGDisplay
                  skills={sectionSkills}
                  evidence={sectionEvidence}
                />
              )}
            </ProgramInfoCardButton>
          </div>
        );
      })}
    </div>
  );
};

export default ProgramSkillsMap;
